// src/utilities/formattedDisplayComponents/JsonEditor.tsx

import React, { useRef, useEffect } from 'react';
import './JsonEditor.css';

interface JsonEditorProps {
  value: string;
  onChange: (value: string) => void;
  readOnly?: boolean;
}

// --- Helper function to apply syntax highlighting ---
function getHighlightedHtml(text: string): string {
  // Escape HTML to prevent injection
  const escapedText = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

  // Regex to find JSON tokens
  return escapedText.replace(
    /(".*?")|(\b(true|false|null)\b)|(-?\d+(\.\d+)?)/g,
    (match, string, keyword, number) => {
      if (string) return `<span class="token-string">${string}</span>`;
      if (keyword) return `<span class="token-boolean">${keyword}</span>`;
      if (number) return `<span class="token-number">${number}</span>`;
      return match;
    }
  );
}

// --- Helper functions to manage cursor position ---
function getCursorPosition(element: HTMLElement): number {
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) return 0;
  
  const range = selection.getRangeAt(0);
  const preSelectionRange = range.cloneRange();
  preSelectionRange.selectNodeContents(element);
  preSelectionRange.setEnd(range.startContainer, range.startOffset);
  
  return preSelectionRange.toString().length;
}

function setCursorPosition(element: HTMLElement, position: number) {
  const selection = window.getSelection();
  if (!selection) return;

  const range = document.createRange();
  let charCount = 0;
  let foundNode = false;

  function findTextNode(node: Node) {
    if (foundNode) return;
    if (node.nodeType === Node.TEXT_NODE) {
      const textNode = node as Text;
      const nextCharCount = charCount + textNode.length;
      if (position <= nextCharCount) {
        range.setStart(textNode, position - charCount);
        range.setEnd(textNode, position - charCount);
        foundNode = true;
      } else {
        charCount = nextCharCount;
      }
    } else {
      for (const childNode of Array.from(node.childNodes)) {
        findTextNode(childNode);
      }
    }
  }

  findTextNode(element);
  selection.removeAllRanges();
  selection.addRange(range);
}

// --- The Editor Component ---
export const JsonEditor: React.FC<JsonEditorProps> = ({ value, onChange, readOnly = false }) => {
  const editorRef = useRef<HTMLPreElement>(null);

  const handleInput = (event: React.FormEvent<HTMLPreElement>) => {
    const element = event.currentTarget;
    const text = element.textContent || '';
    
    const cursorPosition = getCursorPosition(element);
    
    onChange(text); // Notify parent of the raw text change
    
    // We restore the cursor position in the useEffect hook after re-render
    element.setAttribute('data-cursor-pos', cursorPosition.toString());
  };

  useEffect(() => {
    const element = editorRef.current;
    if (!element) return;

    const currentText = element.textContent || '';
    if (currentText !== value) {
      const cursorPositionAttr = element.getAttribute('data-cursor-pos');
      const cursorPosition = cursorPositionAttr ? parseInt(cursorPositionAttr, 10) : value.length;

      element.innerHTML = getHighlightedHtml(value);
      setCursorPosition(element, cursorPosition);
      element.removeAttribute('data-cursor-pos');
    }
  }, [value]);

  return (
    <pre
      ref={editorRef}
      className="json-editor"
      contentEditable={!readOnly}
      onInput={handleInput}
      spellCheck="false"
      suppressContentEditableWarning={true}
    />
  );
};